import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { api } from '../../frontend/components/api';

export function useTwilioVoice(voiceConfiguration) {
  const [status, setStatus] = useState('Initializing...');
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);
  const [connection, setConnection] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [conversation, setConversation] = useState(null);

  const deviceRef = useRef(null);
  const isMountedRef = useRef(true);
  const disconnectTimeoutRef = useRef(null);

  const initializeDevice = useCallback(async () => {
    setStatus('Fetching token...');
    setError(null);
    try {
      const response =
        (await api.fetchTwilioAccessTokenPublic?.(
          voiceConfiguration?.client_token
        )) ?? (await api.fetchTwilioAccessToken?.());
      const { token, conversation } = response || {};
      if (!token) throw new Error('Invalid token received from server.');
      setConversation(conversation || null);

      setStatus('Initializing device...');
      const currentDevice = new Device(token, { logLevel: 'WARN' });
      deviceRef.current = currentDevice;

      currentDevice.on('ready', () => {
        if (isMountedRef.current) {
          setStatus('Ready');
          setDevice(currentDevice);
        }
      });

      currentDevice.on('connect', (conn) => {
        if (!isMountedRef.current) return;
        if (!conn || typeof conn.disconnect !== 'function') {
          setError('Received invalid connection object from Twilio.');
          setStatus('Error');
          return;
        }
        setStatus('On Call');
        setConnection(conn);
        setIsMuted(conn.isMuted());

        const handle = setInterval(() => {
          if (isMountedRef.current && conn.getRemoteStream()) {
            setAudioStream(conn.getRemoteStream());
            clearInterval(handle);
          }
        }, 100);

        conn.on('mute', (muted) => isMountedRef.current && setIsMuted(muted));
        conn.on('disconnect', () => {
          if (!isMountedRef.current) return;
          clearTimeout(disconnectTimeoutRef.current);
          setStatus('Ready');
          setConnection(null);
          setAudioStream(null);
        });
        conn.on('error', (err) => {
          if (!isMountedRef.current) return;
          setError(`Call Error: ${err.message} (Code: ${err.code})`);
          setStatus('Error');
          setConnection(null);
          setAudioStream(null);
        });
      });

      currentDevice.on('disconnect', () => {
        if (isMountedRef.current) {
          clearTimeout(disconnectTimeoutRef.current);
          setStatus('Ready');
          setConnection(null);
          setAudioStream(null);
        }
      });

      currentDevice.on('error', (err) => {
        if (isMountedRef.current) {
          setError(`Device Error: ${err.message} (Code: ${err.code})`);
          setStatus('Error');
        }
      });

      currentDevice.on('offline', () => {
        if (isMountedRef.current) {
          setStatus('Offline');
          setDevice(null);
        }
      });

      await currentDevice.register();
      if (isMountedRef.current && currentDevice.state === 'registered') {
        setStatus('Ready');
        setDevice(currentDevice);
      }
    } catch (err) {
      console.error('Initialization failed:', err);
      if (isMountedRef.current) {
        setError(`Initialization failed: ${err.message}`);
        setStatus('Error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, voiceConfiguration]);

  useEffect(() => {
    if (!voiceConfiguration) return;
    isMountedRef.current = true;
    initializeDevice();

    return () => {
      isMountedRef.current = false;
      clearTimeout(disconnectTimeoutRef.current);
      if (deviceRef.current) {
        deviceRef.current.removeAllListeners();
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      setDevice(null);
      setConnection(null);
      setStatus('Unmounted');
      setError(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeDevice]);

  const handleConnect = useCallback(async () => {
    if (!device) {
      setError('Device not ready.');
      return;
    }
    setStatus('Connecting...');
    try {
      const call = await device.connect({
        params: { To: voiceConfiguration.bot_number },
      });
      if (call && typeof call.disconnect === 'function') {
        setStatus('On Call');
        setConnection(call);
        setIsMuted(call.isMuted());
        const handle = setInterval(() => {
          if (isMountedRef.current && call.getRemoteStream()) {
            setAudioStream(call.getRemoteStream());
            clearInterval(handle);
          }
        }, 100);
        call.on('disconnect', () => {
          if (isMountedRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
            setStatus('Ready');
            setConnection(null);
            setAudioStream(null);
          }
        });
        call.on('error', (err) => {
          if (isMountedRef.current) {
            setError(`Call Error: ${err.message} (Code: ${err.code})`);
            setStatus('Error');
            setConnection(null);
            setAudioStream(null);
          }
        });
        call.on('mute', (muted) => isMountedRef.current && setIsMuted(muted));
      } else {
        setError('Connection attempt returned an invalid object.');
        setStatus('Error');
      }
    } catch (err) {
      setError(`Connection Error: ${err.message}`);
      setStatus('Ready');
    }
  }, [device, voiceConfiguration]);

  const handleDisconnect = useCallback(() => {
    clearTimeout(disconnectTimeoutRef.current);
    if (connection) {
      connection.disconnect();
    } else if (device) {
      device.disconnectAll();
    }
    setStatus('Disconnecting...');
    disconnectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setStatus((prev) => {
          if (prev === 'Disconnecting...') {
            setConnection(null);
            return 'Ready';
          }
          return prev;
        });
      }
    }, 5000);
  }, [connection, device]);

  const handleToggleMute = useCallback(() => {
    if (connection) {
      const shouldMute = !isMuted;
      connection.mute(shouldMute);
      setIsMuted(shouldMute);
    }
  }, [connection, isMuted]);

  return {
    status,
    error,
    device,
    connection,
    isMuted,
    audioStream,
    conversation,
    canConnect: device && (status === 'Ready' || status === 'Offline'),
    canDisconnect: !!connection,
    canToggleMute: !!connection,
    handleConnect,
    handleDisconnect,
    handleToggleMute,
  };
}
